import React from 'react';
import { motion } from 'framer-motion';

export default function Privacy() {
  return (
    <main className="min-h-screen pt-24 pb-16 bg-[#f8fafc]" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 sm:p-12 shadow-sm border border-slate-200"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 font-['Oswald']">
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-sm mb-8 pb-6 border-b border-slate-100">
            Effective Date: 21 July 2026
          </p>

          <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
            <p>
              Welcome to VHFMS (Venkateswara Hospitality & Facilities Management Services). We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your information when you access or use our digital platforms and related services.
            </p>
            <p>
              By using our services, you agree to the practices described in this Privacy Policy.
            </p>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-3 font-['Oswald']">1. Information We Collect</h2>
              <p className="mb-3">We may collect the following information when you use our services:</p>

              <div className="space-y-4 pl-2">
                <div>
                  <h3 className="font-semibold text-slate-900">Personal Information</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700">
                    <li>Full Name</li>
                    <li>Mobile Number</li>
                    <li>Profile Information</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900">Service Information</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700">
                    <li>Booking Details</li>
                    <li>Selected Services</li>
                    <li>Preferred Date and Time</li>
                    <li>Assigned Employee Information</li>
                    <li>Booking History</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900">Location Information</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700">
                    <li>Selected City or Service Location</li>
                    <li>Service Address entered for bookings</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900">Device Information</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700">
                    <li>Device Model</li>
                    <li>Operating System Version</li>
                    <li>Application Version</li>
                    <li>Crash Logs (if applicable)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900">Usage Information</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700">
                    <li>Pages Visited</li>
                    <li>Features Used</li>
                    <li>Platform Activity</li>
                    <li>Notification Preferences</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">2. How We Use Your Information</h2>
              <p className="mb-2">We use your information to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Create and manage your account.</li>
                <li>Process and manage service requests and bookings.</li>
                <li>Assign service professionals to your requests.</li>
                <li>Send booking confirmations, updates, and notifications.</li>
                <li>Improve our services and user experience.</li>
                <li>Provide customer support.</li>
                <li>Detect and prevent fraud or misuse.</li>
                <li>Comply with legal obligations.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">3. Information Sharing</h2>
              <p className="mb-2">We do not sell or rent your personal information.</p>
              <p className="mb-2">Your information may be shared only with:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Service professionals assigned to your request.</li>
                <li>Trusted technology service providers that help operate and maintain our platform.</li>
                <li>Government or legal authorities when required by law.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">4. Data Storage and Security</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>We take appropriate technical and organizational measures to protect your information from unauthorized access, loss, misuse, or disclosure.</li>
                <li>Customer data is stored securely using trusted cloud infrastructure and access controls.</li>
                <li>Although we strive to protect your information, no method of electronic storage or internet transmission is completely secure.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">5. Third-Party Services</h2>
              <p className="mb-2">
                Our platform may use trusted third-party services to provide certain features, including cloud storage, authentication, analytics, and communication services.
              </p>
              <p>
                These services process information only as necessary to provide our services and maintain platform security.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">6. Permissions Used</h2>
              <p className="mb-2">Depending on your device and the features you use, our platform may request permissions such as:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Internet Access</li>
                <li>Notifications</li>
                <li>Location (only if required for service availability or address selection)</li>
                <li>Camera or Storage (only if you upload images or documents in future features)</li>
              </ul>
              <p className="mt-2">Permissions are requested only when necessary.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">7. User Responsibilities</h2>
              <p className="mb-2">Users are responsible for:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Providing accurate information.</li>
                <li>Keeping login credentials secure.</li>
                <li>Using the platform responsibly.</li>
                <li>Informing us of any unauthorized account access.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">8. Data Retention</h2>
              <p className="mb-2">We retain your information only for as long as necessary to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Provide requested services.</li>
                <li>Maintain booking history.</li>
                <li>Meet legal or regulatory requirements.</li>
                <li>Resolve disputes and enforce agreements.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">9. Children's Privacy</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Our services are not intended for children under the age of 13.</li>
                <li>We do not knowingly collect personal information from children.</li>
                <li>If we become aware that such information has been collected, we will take appropriate steps to remove it.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">10. Changes to This Privacy Policy</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>We may update this Privacy Policy from time to time.</li>
                <li>Any updates will be published on this page with a revised Effective Date.</li>
                <li>We encourage users to review this Privacy Policy periodically.</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">11. Contact Us</h2>
              <p className="mb-2">If you have any questions or concerns regarding this Privacy Policy, please contact us:</p>
              <p className="font-semibold text-slate-900">Company: VHFMS (Venkateswara Hospitality & Facilities Management Services)</p>
              <p>Website: <a href="http://www.vhfms.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.vhfms.com</a></p>
              <p>Email: <a href="mailto:vhfms.info@gmail.com" className="text-blue-600 hover:underline">vhfms.info@gmail.com</a></p>
              <p>Phone: +91-9900590051</p>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <h2 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">12. Consent</h2>
              <p>
                By accessing or using VHFMS services, you acknowledge that you have read, understood, and agree to this Privacy Policy.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
